import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Grid,
    Typography,
    Paper,
    CircularProgress,
    Button,
    Modal,
    Box,
    Divider,
} from "@mui/material";
import { Description, PictureAsPdf, AccountBalance, Person } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import baseURL from "../../config";

const CrewingAgentProfile = () => {
    const { id: crewingAgentId } = useParams();
    const [crewingAgent, setCrewingAgent] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDocumentModal, setOpenDocumentModal] = useState(false);

    const fetchCrewingAgent = async () => {
        if (!crewingAgentId) return;
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${baseURL}/api/getCrewingAgentsDetailsById/${crewingAgentId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCrewingAgent(response.data);
        } catch (error) {
            console.error("Error fetching crewing agent details:", error);
        }
    };

    const fetchDocuments = async () => {
        if (!crewingAgentId) return;
        try {
            setLoading(true);
            const response = await axios.get(`${baseURL}/api/getDocumentsForAgent/${crewingAgentId}`);
            setDocuments(response.data.documentTypes || []);
        } catch (error) {
            console.error("Error fetching documents:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCrewingAgent();
        fetchDocuments();
    }, [crewingAgentId]);

    const CardWrapper = ({ icon, title, children }) => (
        <Paper
            elevation={4}
            sx={{
                p: 3,
                borderRadius: 4,
                backgroundColor: "#fff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
        >
            <Box display="flex" alignItems="center" mb={2}>
                {icon}
                <Typography variant="h6" fontWeight="bold" ml={1} color="primary">
                    {title}
                </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {children}
        </Paper>
    );

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ color: "#A90A10" }} gutterBottom>
                Crewing Agent Profile
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <CardWrapper
                        icon={<Person sx={{ color: "#A90A10" }} />} // Apply red color to icon
                        title={<span style={{ color: "#A90A10" }}>Agent Details</span>} // Apply red color to title
                    >
                        {crewingAgent ? (
                            <>
                                {[
                                    ["Name", crewingAgent.agentName],
                                    ["Short Name", crewingAgent.shortName],
                                    ["Contact Person", crewingAgent.contactPerson ? `${crewingAgent.contactPerson.title} ${crewingAgent.contactPerson.name}` : "N/A"],
                                    ["Contact No", crewingAgent.contactNumber],
                                    ["Email", crewingAgent.email],
                                    ["Address", crewingAgent.address],
                                    ["Country", crewingAgent.country],
                                    ["Audit Status", crewingAgent.AuditStatus],
                                    ["Audit Date", crewingAgent.Audit && new Date(crewingAgent.Audit).toLocaleDateString()],
                                    ["ISO Cert Issue Date", crewingAgent.ISOCertificateIssuedDate && new Date(crewingAgent.ISOCertificateIssuedDate).toLocaleDateString()],
                                    ["ISO Cert Expiry Date", crewingAgent.ISOCertificateExpiryDate && new Date(crewingAgent.ISOCertificateExpiryDate).toLocaleDateString()],
                                    ["RPS License Issue Date", crewingAgent.RPSLicenseIssuedDate && new Date(crewingAgent.RPSLicenseIssuedDate).toLocaleDateString()],
                                    ["RPS License Expiry Date", crewingAgent.RPSLicenseExpiryDate && new Date(crewingAgent.RPSLicenseExpiryDate).toLocaleDateString()],
                                ].map(([label, value]) => (
                                    <Typography key={label} sx={{ mb: 1 }}>
                                        <strong>{label}:</strong> {value || "N/A"}
                                    </Typography>
                                ))}
                            </>
                        ) : (
                            <Typography color="textSecondary">Loading...</Typography>
                        )}
                    </CardWrapper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <CardWrapper
                        icon={<Description sx={{ color: "#AD0A11" }} />}
                        title={<span style={{ color: "#AD0A11" }}>Agent Documents</span>}
                    >
                        {["ISOCertificate", "RPSLicense", "cirtificatteOfIncorporation"].map((docKey) => {
                            const doc = crewingAgent?.[docKey];
                            const downloadURL = doc?.filePath ? `${baseURL}/${doc.filePath.replace(/\\/g, "/")}` : null;

                            return (
                                <Paper
                                    key={docKey}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 2,
                                        mb: 2,
                                        borderRadius: 2,
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                                    }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <PictureAsPdf sx={{ color: "red", mr: 1 }} />
                                        <Typography fontWeight={500}>{docKey}</Typography>
                                    </Box>
                                    {downloadURL ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            href={downloadURL}
                                            target="_blank"
                                            sx={{ borderRadius: 2 , color:"#AD0A11", borderColor: "#AD0A11",}}
                                        >
                                            Download
                                        </Button>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            Not Available
                                        </Typography>
                                    )}
                                </Paper>
                            );
                        })}

                        <Divider sx={{ my: 2 }} />

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Description />}
                            onClick={() => setOpenDocumentModal(true)}
                            disabled={loading}
                            sx={{ borderRadius: 2, py: 1 , backgroundColor: "#AD0A11"}}
                        >
                            View Contracts
                        </Button>

                        {loading && (
                            <Box display="flex" justifyContent="center" mt={2}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </CardWrapper>
                </Grid>
            </Grid>

            <Modal open={openDocumentModal} onClose={() => setOpenDocumentModal(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: "70%", md: "50%" },
                        maxHeight: "80vh",
                        bgcolor: "white",
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 4,
                        overflowY: "auto",
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" color="primary" mb={3} textAlign="center">
                        Contracts Documents
                    </Typography>

                    {Object.keys(documents).length > 0 ? (
                        Object.keys(documents).map((key) => {
                            let path = documents[key].path?.replace(/\\/g, "/") || "";
                            let downloadURL = path.startsWith("http") ? path : `${baseURL}/${path}`;

                            return (
                                <Paper
                                    key={key}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 2,
                                        mb: 2,
                                        borderRadius: 3,
                                        boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
                                    }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <PictureAsPdf sx={{ color: "red", mr: 1 }} />
                                        <Typography>{key}: {documents[key].filename || "Unknown"}</Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        href={downloadURL}
                                        target="_blank"
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Download
                                    </Button>
                                </Paper>
                            );
                        })
                    ) : (
                        <Typography align="center" color="textSecondary">
                            No documents available
                        </Typography>
                    )}

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Button variant="contained" color="error" onClick={() => setOpenDocumentModal(false)} sx={{ borderRadius: 2 }}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default CrewingAgentProfile;
